import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, InputLabel, TextField } from '@mui/material';
import { useAppDispatch } from '~/redux/hooks';
import { IEditCompanyRole } from '~/utils/interface/company-role';
import { useTranslation } from 'react-i18next';
import { BaseRow } from '~/components/common';
import { handleCreateCompanyRole, handleUpdateCompanyRole } from '~/thunks/company-role/companyRoleThunk';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, findRecursiveItem, flatRecursive, handleApiError, notify } from '~/utils/helper';
import { companyRoleActions } from '~/thunks/company-role/companyRoleSlice';
import { COMPANY_ROLE_GROUPS } from '~/utils/constants/company-role';
import { IObject } from '~/utils/interface/common';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view';
import { uniq } from 'lodash';
import i18n from '~/utils/i18n';

type CompanyRoleListProps = {
  editData?: IEditCompanyRole;
  mode: string;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IEditCompanyRole = {
  name: '',
  permissions: [],
};

function CompanyRoleList(props: CompanyRoleListProps) {
  //#region Destructuring Props
  const { mode } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/company-role']);
  const dispatch = useAppDispatch();

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateCompanyRole(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(companyRoleActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(
            handleUpdateCompanyRole({
              id: Number(props.editData?.id),
              params: values,
            })
          ).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(companyRoleActions.changeNeedRefreshData(true));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [expandedItems, setExpandedItems] = useState<string[]>(['project', 'setting', 'template']);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.editData) {
      form.setFieldValue('name', cast(props.editData.name));
      form.setFieldValue('permissions', props.editData.permissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editData]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    props.setOpen(open, needRefreshData);
  };

  const renderCheckboxComponent = useCallback(
    (value: string) => {
      let tranKey = value;
      if (!i18n.exists(`permissions.${value}`, { ns: 'admin/company-role' })) {
        const tranKeyArray = value && value.split('_');
        tranKey = tranKeyArray[tranKeyArray.length - 1];
      }

      return <TreeItem itemId={value} label={t(`permissions.${tranKey}`)} />;
    },
    [t]
  );

  const renderParentCheckboxComponent = useCallback(
    (value: string, child: ReactNode) => {
      return (
        <TreeItem itemId={value} label={t(`permissions.${value}`)} className='mt-[10px]'>
          {child}
        </TreeItem>
      );
    },
    [t]
  );

  function renderCompanyRoleRecursive(companyRoles: IObject): ReactNode {
    const componentList = [];
    for (let i in companyRoles) {
      const companyRole: IObject = companyRoles[i as keyof IObject];
      if (typeof companyRole === 'object') {
        componentList.push(renderParentCheckboxComponent(i, renderCompanyRoleRecursive(companyRole)));
      } else if (typeof companyRole === 'string') {
        componentList.push(renderCheckboxComponent(companyRole));
      }
    }
    return componentList.map((item) => item);
  }

  const onItemClick = (event: any, itemId: string) => {
    // Only handle if user click on the checkbox
    if (event.target?.tagName !== 'INPUT') return;

    const foundPermissions = findRecursiveItem(itemId, COMPANY_ROLE_GROUPS);
    const flatPermissionList = [itemId, ...flatRecursive(foundPermissions, [], true)];

    const currentSelectPermission = form.values.permissions.indexOf(itemId) > -1;
    if (!currentSelectPermission) {
      form.setFieldValue('permissions', uniq([...form.values.permissions, ...flatPermissionList]));
    } else {
      form.setFieldValue(
        'permissions',
        form.values.permissions.filter((item) => flatPermissionList.indexOf(item) === -1)
      );
    }
  };

  const CompanyRoleComponent = memo(() => {
    return (
      <SimpleTreeView
        multiSelect
        checkboxSelection
        selectedItems={form.values.permissions}
        expandedItems={expandedItems}
        onExpandedItemsChange={(_event, itemIds: string[]) => setExpandedItems(itemIds)}
        onItemClick={onItemClick}
      >
        {renderCompanyRoleRecursive(COMPANY_ROLE_GROUPS)}
      </SimpleTreeView>
    );
  });

  //#endregion Handle Function

  return (
    <Card>
      <CardContent>
        <BaseRow className='w-full mb-[10px]'>
          <Box className='flex gap-[10px] ml-auto'>
            <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
              {t('save', { ns: 'admin/common' })}
            </Button>
          </Box>
        </BaseRow>

        <BaseRow className='w-full gap-[50px]'>
          <Box className='w-full mb-[20px]'>
            <InputLabel>{t('property.name')}</InputLabel>
            <TextField
              name='name'
              placeholder={t('property.name')}
              value={cast(form.values.name)}
              onChange={form.handleChange}
              error={Boolean(form.errors.name)}
              helperText={form.errors.name}
            />
          </Box>
        </BaseRow>

        <BaseRow className='mb-[20px] '>
          <Box className='flex flex-col overflow-auto w-full h-[calc(100vh-420px)]'>
            <CompanyRoleComponent />
          </Box>
        </BaseRow>
      </CardContent>
    </Card>
  );
}

export default memo(CompanyRoleList);
